import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { getActivityAnchorId } from '@aline/common';
import {
  Avatar,
  ButtonBase,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { ChevronDown, Link as LinkIcon, Mail, MoreHorizontal } from 'lucide-react';
import type { OnSubmitCommentReturn } from 'src/components';
import { useDispatchNotification } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { ActivityReplyComposer } from './ActivityReplyComposer';
import type { EmailThread, EmailThreadMessage } from './email-thread';
import {
  formatCollapsedMessageTimestamp,
  formatMessageTimestamp,
  getLatestMessage,
  getMessagePreview,
  getThreadParticipantNames,
} from './email-thread';
import { EmailMessageDetails } from './EmailMessageDetails';
import { EmailParticipantAvatar } from './EmailParticipantAvatar';

const useStyles = makeStyles()(theme => ({
  card: {
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    boxShadow: '0px 0px 3.4px rgba(0,0,0,0.05)',
    padding: theme.spacing(1.5, 1.5, 2),
  },
  cardContents: {
    paddingLeft: 54,
    paddingTop: theme.spacing(2),
  },
  cardHeading: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  chevron: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  chevronExpanded: {
    transform: 'rotate(180deg)',
  },
  envelope: {
    backgroundColor: theme.appColors.selectedBackgroundNav,
    flexShrink: 0,
    height: 38,
    width: 38,
  },
  headingText: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  kind: {
    color: theme.appColors.primary,
    fontWeight: 600,
  },
  kindRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  message: {
    borderBottom: `1px solid ${theme.appColors.ruleSubtle}`,
  },
  messageContents: {
    padding: theme.spacing(0, 1.5, 1.5, 6.75),
  },
  messageMeta: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  messageMetaTop: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  messageRow: {
    '&.Mui-focusVisible': {
      backgroundColor: theme.appColors.hoverBackgroundSubtle,
      outline: `2px solid ${theme.appColors.primary}`,
      outlineOffset: -2,
    },
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackgroundSubtle,
    },
    gap: 12,
    justifyContent: 'flex-start',
    padding: theme.spacing(1, 1.5),
    textAlign: 'left',
    width: '100%',
  },
  messageRowExpanded: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
  },
  replyDock: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    flexShrink: 0,
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  secondary: {
    color: theme.palette.text.secondary,
    lineHeight: '16px',
  },
  senderName: {
    fontWeight: 600,
    lineHeight: '16px',
  },
  subject: {
    flex: 1,
    lineHeight: '22px',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  threadHeader: {
    borderBottom: `1px solid ${theme.appColors.ruleSubtle}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: theme.spacing(1.5, 0, 2),
  },
  time: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  timestamp: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  titleRow: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface EmailThreadViewProps {
  thread: EmailThread;
  /** Replies to the anchor email; the server sends them to the thread as an email. */
  onSubmitReply?: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
}

/**
 * An email thread opened from the activity list: one message as a card, or every message. The
 * panel header around it carries the way back to the list.
 */
export const EmailThreadView = React.memo(function EmailThreadView({
  onSubmitReply,
  thread,
}: EmailThreadViewProps) {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.scroller}>
        <EmailThreadBody thread={thread} />
      </div>
      {onSubmitReply && (
        <div className={classes.replyDock}>
          <ActivityReplyComposer
            commentId={thread.messages[0].commentId}
            placeholder="Reply to this email..."
            onSubmitReply={onSubmitReply}
          />
        </div>
      )}
    </div>
  );
});

function EmailThreadBody({ thread }: { thread: EmailThread }): JSX.Element {
  if (thread.messages.length === 1)
    return <SingleEmailCard message={thread.messages[0]} thread={thread} />;
  return <LongEmailThread thread={thread} />;
}

function EmailThreadOptionsMenu({ activityItemId }: { activityItemId: UUID }): JSX.Element {
  const { theme } = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const dispatchLinkCopied = useDispatchNotification({
    message: 'Email link copied',
    variant: 'success',
  });
  const dispatchCopyFailed = useDispatchNotification({
    message: "Couldn't copy email link",
    variant: 'error',
  });

  function handleCopyLink(): void {
    setMenuAnchorEl(null);
    const url = new URL(window.location.href);
    url.hash = getActivityAnchorId(activityItemId);
    navigator.clipboard
      .writeText(url.toString())
      .then(dispatchLinkCopied)
      .catch(dispatchCopyFailed);
  }

  return (
    <>
      <IconButton
        aria-label="Email options"
        size="small"
        onClick={event => setMenuAnchorEl(event.currentTarget)}
      >
        <MoreHorizontal color={theme.appColors.iconsType} size={18} />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(menuAnchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem dense onClick={handleCopyLink}>
          <LinkIcon size={16} style={{ marginRight: theme.spacing(1), opacity: 0.75 }} />
          Copy link to email
        </MenuItem>
      </Menu>
    </>
  );
}

function SingleEmailCard({
  message,
  thread,
}: {
  message: EmailThreadMessage;
  thread: EmailThread;
}): JSX.Element {
  const { classes, theme } = useStyles();

  return (
    <div className={classes.card}>
      <div className={classes.cardHeading}>
        <Avatar aria-hidden className={classes.envelope}>
          <Mail color={theme.appColors.primary} size={18} />
        </Avatar>
        <div className={classes.headingText}>
          <div className={classes.kindRow}>
            <Typography className={classes.kind} variant="caption">
              Email
            </Typography>
            <Typography className={classes.timestamp} variant="caption">
              {formatMessageTimestamp(message.createdAt)}
            </Typography>
          </div>
          <div className={classes.titleRow}>
            <Typography className={classes.subject} component="h2" variant="subtitle1">
              {thread.subject}
            </Typography>
            <EmailThreadOptionsMenu activityItemId={thread.activityItemId} />
          </div>
        </div>
      </div>
      <div className={classes.cardContents}>
        <EmailMessageDetails spacious message={message} thread={thread} />
      </div>
    </div>
  );
}

const EmailThreadMessageRow = React.memo(function EmailThreadMessageRow({
  expanded,
  message,
  onToggle,
  thread,
}: {
  expanded: boolean;
  message: EmailThreadMessage;
  onToggle: (commentId: UUID) => void;
  thread: EmailThread;
}) {
  const { classes, cx } = useStyles();
  const handleToggle = useCallback(
    () => onToggle(message.commentId),
    [message.commentId, onToggle]
  );
  const timestamp = expanded
    ? formatMessageTimestamp(message.createdAt)
    : formatCollapsedMessageTimestamp(message.createdAt);

  return (
    <div className={classes.message}>
      <ButtonBase
        aria-expanded={expanded}
        className={cx(classes.messageRow, expanded && classes.messageRowExpanded)}
        onClick={handleToggle}
      >
        <EmailParticipantAvatar
          colorKey={message.fromAddress ?? message.senderName}
          isAssistant={message.isAiAuthored}
          name={message.senderName}
        />
        <span className={classes.messageMeta}>
          <span className={classes.messageMetaTop}>
            <Typography noWrap className={classes.senderName} variant="caption">
              {message.senderName}
            </Typography>
            <Typography className={cx(classes.secondary, classes.time)} variant="caption">
              {timestamp}
            </Typography>
          </span>
          {!expanded && (
            <Typography noWrap className={classes.secondary} variant="caption">
              {getMessagePreview(message.text)}
            </Typography>
          )}
        </span>
        <ChevronDown
          className={cx(classes.chevron, expanded && classes.chevronExpanded)}
          size={12}
        />
      </ButtonBase>
      <Collapse unmountOnExit in={expanded}>
        <div className={classes.messageContents}>
          <EmailMessageDetails message={message} thread={thread} />
        </div>
      </Collapse>
    </div>
  );
});

function LongEmailThread({ thread }: { thread: EmailThread }): JSX.Element {
  const { classes } = useStyles();
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<UUID>>(
    () => new Set([getLatestMessage(thread).commentId])
  );
  const toggle = useCallback((commentId: UUID) => {
    setExpandedIds(previous => {
      const next = new Set(previous);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  }, []);
  const participants = getThreadParticipantNames(thread).join(', ');

  return (
    <div>
      <div className={classes.threadHeader}>
        <div className={classes.titleRow}>
          <Typography className={classes.subject} component="h2" variant="subtitle1">
            {thread.subject}
          </Typography>
          <EmailThreadOptionsMenu activityItemId={thread.activityItemId} />
        </div>
        <Typography className={classes.secondary} variant="caption">
          {`${thread.messages.length} messages · ${participants}`}
        </Typography>
      </div>
      {thread.messages.map(message => (
        <EmailThreadMessageRow
          key={message.commentId}
          expanded={expandedIds.has(message.commentId)}
          message={message}
          thread={thread}
          onToggle={toggle}
        />
      ))}
    </div>
  );
}
